// frontend/src/components/admin/AdminLayout.js
import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { serviceOpsApi } from '../../api/serviceOps';
import { supportApi } from '../../api/support';
import { bulkApi } from '../../api/bulk'; 
import { reviewsApi } from '../../api/reviews'; 
import { walletApi } from '../../api/wallet'; 
import { 
  FaTachometerAlt, FaBox, FaTools, FaCar, FaUsers, FaSignOutAlt, 
  FaWarehouse, FaBars, FaTimes, FaMapMarkedAlt, FaStore, FaNetworkWired, FaHome, FaTruck, FaCogs, FaHeadset, FaBoxes, FaChartPie, FaShippingFast, FaRobot, FaTags, FaMoneyBillWave, FaPenNib, FaChartBar, FaBrain, FaStar, FaBell, FaMoneyCheckAlt, FaBuilding, FaShieldAlt, FaUndo, FaCommentDots, FaWallet 
} from 'react-icons/fa';

const MASTER_ADMIN_EMAILS = ["esekiegabriel@gmail.com", "thomasesekie@gmail.com"];

function AdminLayout() {
  const { logout, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [pendingAppsCount, setPendingAppsCount] = useState(0);
  const [openTicketsCount, setOpenTicketsCount] = useState(0);
  const [pendingBulkCount, setPendingBulkCount] = useState(0); 
  const [pendingReviewsCount, setPendingReviewsCount] = useState(0); 
  const [pendingPayoutsCount, setPendingPayoutsCount] = useState(0); 

  const hasAccess = (item) => {
      if (user?.email && MASTER_ADMIN_EMAILS.includes(user.email.toLowerCase())) return true;
      if (user?.permissions && Array.isArray(user.permissions.allowed_tabs)) {
          return user.permissions.allowed_tabs.includes(item.id);
      }
      if (!user || !user.roles) return false;
      if (item.roles.includes('all')) return true;
      return user.roles.some(role => item.roles.includes(role));
  };

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        if (user?.roles?.some(r => ['admin', 'super_admin'].includes(r))) {
            const [apps, tickets, bulk, reviews, payouts] = await Promise.all([
                serviceOpsApi.getApplications('pending'),
                supportApi.getAllTicketsAdmin('open'),
                bulkApi.getAllRequestsAdmin('pending'),
                reviewsApi.getAllReviewsAdmin('pending'),
                walletApi.getAllPayouts('pending') 
            ]);
            setPendingAppsCount(apps.length);
            setOpenTicketsCount(tickets.length);
            setPendingBulkCount(bulk.length);
            setPendingReviewsCount(reviews.length); 
            setPendingPayoutsCount(payouts.length); 
        }
      } catch (e) {
        console.error("Failed to fetch admin counts", e);
      }
    };
    fetchCounts();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { id: 'dashboard', path: '/admin', label: 'Dashboard', icon: FaTachometerAlt, roles: ['all'] },
    { id: 'ea_hub', path: '/admin/ea-hub', label: 'EA Hub (Exclusive)', icon: FaBuilding, roles: ['super_admin', 'admin'] },
    { id: 'ai_analyst', path: '/admin/executive-analyst', label: 'AI Executive Analyst', icon: FaBrain, roles: ['super_admin', 'admin'] },
    { id: 'retention', path: '/admin/retention', label: 'Retention Engine', icon: FaBell, roles: ['super_admin', 'admin'] },
    { id: 'analytics', path: '/admin/analytics', label: 'Deep Analytics', icon: FaChartPie, roles: ['super_admin', 'admin'] },
    { id: 'finance', path: '/admin/finance', label: 'Financials', icon: FaMoneyBillWave, roles: ['super_admin', 'admin'] },
    { id: 'payouts', path: '/admin/payouts', label: 'Payout Requests', icon: FaMoneyCheckAlt, badge: pendingPayoutsCount > 0 ? pendingPayoutsCount : null, roles: ['super_admin', 'admin'] }, 
    { id: 'fund_wallet', path: '/admin/fund-wallet', label: 'Fund Wallet', icon: FaWallet, roles: ['super_admin', 'admin'] }, 
    { id: 'reports', path: '/admin/reports', label: 'Inventory Reports', icon: FaChartBar, roles: ['super_admin', 'admin', 'store_manager'] },
    { id: 'orders', path: '/admin/orders', label: 'Orders', icon: FaBox, roles: ['super_admin', 'admin', 'store_manager', 'territory_coordinator'] },
    { id: 'delivery', path: '/admin/delivery', label: 'Logistics & Delivery', icon: FaTruck, roles: ['super_admin', 'admin', 'logistics_manager'] },
    { id: 'shipping', path: '/admin/shipping', label: 'Shipping Rates', icon: FaShippingFast, roles: ['super_admin', 'admin'] },
    { id: 'services', path: '/admin/services', label: 'Service Bookings', icon: FaTools, roles: ['super_admin', 'admin', 'territory_coordinator'] },
    { id: 'bulk', path: '/admin/bulk', label: 'Bulk Requests', icon: FaBoxes, badge: pendingBulkCount > 0 ? pendingBulkCount : null, roles: ['super_admin', 'admin'] },
    { id: 'support', path: '/admin/support', label: 'Support Desk', icon: FaHeadset, badge: openTicketsCount > 0 ? openTicketsCount : null, roles: ['super_admin', 'admin'] },
    { id: 'reviews', path: '/admin/reviews', label: 'Review Moderation', icon: FaStar, badge: pendingReviewsCount > 0 ? pendingReviewsCount : null, roles: ['super_admin', 'admin'] }, 
    { id: 'ai_logs', path: '/admin/ai-logs', label: 'AI Chat Logs', icon: FaRobot, roles: ['super_admin', 'admin'] },
    { id: 'providers', path: '/admin/providers', label: 'Service Network', icon: FaNetworkWired, badge: pendingAppsCount > 0 ? pendingAppsCount : null, roles: ['super_admin', 'admin', 'territory_coordinator'] },
    { id: 'inventory', path: '/admin/inventory', label: 'Parts Inventory', icon: FaWarehouse, roles: ['super_admin', 'admin'] },
    { id: 'catalog', path: '/admin/catalog', label: 'Catalog Management', icon: FaTags, roles: ['super_admin', 'admin'] },
    { id: 'vehicles', path: '/admin/vehicles', label: 'Vehicle Marketplace', icon: FaCar, roles: ['super_admin', 'admin'] },
    { id: 'users', path: '/admin/users', label: 'User Management', icon: FaUsers, roles: ['super_admin', 'admin', 'territory_coordinator'] },
    { id: 'zones', path: '/admin/zones', label: 'Zone Management', icon: FaMapMarkedAlt, roles: ['super_admin', 'admin'] },
    { id: 'stores', path: '/admin/stores', label: 'Store Management', icon: FaStore, roles: ['super_admin', 'admin'] },
    { id: 'content', path: '/admin/content', label: 'Content CMS', icon: FaPenNib, roles: ['super_admin', 'admin'] },
    { id: 'settings', path: '/admin/settings', label: 'System Settings', icon: FaCogs, roles: ['super_admin', 'admin'] },
    
    // --- NEW NOTIFICATION SETTINGS TAB ---
    { 
        id: 'notifications', 
        path: '/admin/notifications', 
        label: 'Notification Settings', 
        icon: FaCommentDots, 
        restricted: true, 
        color: 'text-purple-500',
        roles: ['super_admin'] 
    },

    // --- RESTRICTED ITEMS ---
    { id: 'ai_system', path: '/admin/ai-system', label: 'AI System Settings', icon: FaShieldAlt, restricted: true, color: 'text-red-500', roles: ['super_admin'] },
    { id: 'reset', path: '/admin/reset', label: 'Reset to Default', icon: FaUndo, restricted: true, color: 'text-red-600', roles: ['super_admin'] },
  ];

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {isSidebarOpen && <div className="fixed inset-0 z-20 bg-black bg-opacity-50 transition-opacity lg:hidden" onClick={() => setIsSidebarOpen(false)}></div>}
      <div className={`fixed inset-y-0 left-0 z-30 w-64 bg-black text-white transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col shadow-xl ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between p-6 border-b border-gray-800 h-20 bg-gray-900">
          <div><h2 className="text-xl font-bold text-white tracking-wider">ADMIN</h2><p className="text-[10px] text-gray-400 uppercase tracking-widest">Control Center</p></div>
          <button onClick={() => setIsSidebarOpen(false)} className="lg:hidden text-white"><FaTimes size={24} /></button>
        </div>
        <nav className="flex-grow py-6 overflow-y-auto bg-gray-900 flex flex-col">
          <ul className="space-y-2 px-3 mb-auto">
            {menuItems.map((item) => {
              // Check restricted items against Master Admin list
              if (item.restricted && (!user?.email || !MASTER_ADMIN_EMAILS.includes(user.email.toLowerCase()))) return null;
              if (!hasAccess(item)) return null;
              const isActive = item.path === '/admin' ? location.pathname === '/admin' : location.pathname.startsWith(item.path);
              return (
                <li key={item.path}>
                  <Link to={item.path} onClick={() => setIsSidebarOpen(false)} className={`flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${isActive ? 'bg-blue-600 text-white shadow-md' : 'text-gray-300 hover:bg-gray-800 hover:text-white'}`}>
                    <div className="flex items-center"><item.icon className={`mr-3 text-lg ${item.color ? item.color : (isActive ? 'text-white' : 'text-gray-400')}`} />{item.label}</div>
                    {/* FIXED: Changed bg-red-50 to bg-red-600 to provide solid red background behind the white number text */}
                    {item.badge && <span className="bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">{item.badge}</span>}
                  </Link>
                </li>
              );
            })}
          </ul>
          <div className="px-3 mt-4 mb-2"><Link to="/" className="flex items-center px-4 py-3 text-sm font-medium text-gray-400 rounded-lg hover:bg-gray-800 hover:text-white transition-colors border border-gray-700"><FaHome className="mr-3 text-lg" />Back to Website</Link></div>
        </nav>
        <div className="p-4 border-t border-gray-800 bg-gray-900">
          <div className="flex items-center mb-4 px-2">
            <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0 text-white">{user?.full_name?.charAt(0) || 'A'}</div>
            <div className="overflow-hidden"><p className="text-sm font-medium text-white truncate">{user?.full_name || 'Admin'}</p><p className="text-xs text-gray-400 truncate">{user?.email}</p></div>
          </div>
          <button onClick={handleLogout} className="w-full flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded transition-colors shadow-sm"><FaSignOutAlt className="mr-2" /> Logout</button>
        </div>
      </div>
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200 lg:hidden shadow-sm z-10">
          <div className="flex items-center"><button onClick={() => setIsSidebarOpen(true)} className="text-gray-600 focus:outline-none lg:hidden"><FaBars size={24} /></button><span className="ml-4 text-lg font-semibold text-gray-800">Dashboard</span></div>
        </header>
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 p-4 md:p-8"><Outlet /></main>
      </div>
    </div>
  );
}

export default AdminLayout;